'use client';

import { useEffect, useState } from 'react';

/**
 * Délai (ms) standard entre l'affichage d'une question et la révélation de ses
 * choix — « le temps de lire » avant de proposer de répondre. Partagé par le
 * player élève (`apps/app`), le défi marketing et le showcase autoplay pour que
 * la cadence soit identique partout.
 */
export const READING_REVEAL_DELAY_MS = 2500;

/**
 * Révèle un élément (typiquement les choix d'un QCM) après `delayMs`, sauf en
 * mode `immediate` (déjà répondu, ou rien à lire) où il est visible d'emblée.
 *
 * - `key` identifie la question courante : à son changement, le compteur est
 *   réinitialisé **de façon SYNCHRONE pendant le render**. Sans ça, `shown`
 *   resterait à `true` (hérité de la question précédente) le temps d'un render,
 *   et les choix de la nouvelle question clignoteraient avant de se cacher.
 * - le timer est nettoyé à chaque changement de `key`/`delayMs`/`immediate`.
 *
 * Côté CSS, n'appliquer la transition d'opacité qu'à l'état VISIBLE (fondu
 * d'entrée) : à l'état caché, pas de classe de transition → masquage instantané,
 * sinon le même nœud DOM animerait un fondu de SORTIE au changement de question.
 */
export function useDelayedReveal({
  key,
  delayMs = READING_REVEAL_DELAY_MS,
  immediate = false,
}: {
  key: string;
  delayMs?: number;
  immediate?: boolean;
}): boolean {
  const [shown, setShown] = useState(immediate);
  const [lastKey, setLastKey] = useState(key);
  if (key !== lastKey) {
    setLastKey(key);
    setShown(immediate);
  }
  useEffect(() => {
    if (immediate) {
      setShown(true);
      return;
    }
    const id = setTimeout(() => setShown(true), delayMs);
    return () => clearTimeout(id);
  }, [key, delayMs, immediate]);
  return shown;
}
